import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, ShieldCheck, LogOut, ShoppingBag } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";

const links = [
  { label: "Universe", href: "#universe" },
  { label: "Comics", href: "#comics" },
  { label: "Characters", href: "#characters" },
  { label: "Lore", href: "#lore" },
  { label: "Join", href: "#join" },
];

export const Navbar = ({ logoImage = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const { count, setOpen: setCartOpen } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" }), 450);
    } else {
      document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, delay: 2.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <nav className="max-w-[1500px] mx-auto px-5 sm:px-8 h-[68px] flex items-center justify-between">
        <Link
          to="/"
          onClick={() => { setOpen(false); if (location.pathname === "/") window.scrollTo({ top: 0, behavior: "smooth" }); }}
          className="flex items-center group"
          data-testid="nav-logo"
        >
          {logoImage ? (
            <img src="/naijaman-logo.png" alt="NAIJAMAN" className="h-8 sm:h-10 w-auto drop-shadow-[0_0_16px_rgba(16,185,129,0.5)]" />
          ) : (
            <span className="font-display text-2xl sm:text-3xl tracking-wide">
              <span className="text-emerald-400 text-glow-green">NAIJA</span><span className="text-amber-500">MAN</span>
            </span>
          )}
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              className="font-accent text-lg tracking-wider text-gray-300 hover:text-emerald-400 transition-colors duration-200"
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </button>
          ))}
          <Link to="/merch" className="font-accent text-lg tracking-wider text-amber-400 hover:text-amber-300 transition-colors duration-200" data-testid="nav-link-store">
            Store
          </Link>
        </div>

        <div className="hidden md:flex items-center gap-3">
          <button onClick={() => setCartOpen(true)} className="relative text-gray-200 hover:text-emerald-400 transition-colors" data-testid="nav-cart-btn" aria-label="cart">
            <ShoppingBag size={22} />
            {count > 0 && (
              <span className="absolute -top-2 -right-2 bg-emerald-500 text-emerald-950 text-xs font-bold rounded-full w-5 h-5 grid place-items-center" data-testid="nav-cart-count">{count}</span>
            )}
          </button>
          {user === false && (
            <>
              <Link to="/login" className="font-accent text-lg tracking-wider text-gray-200 hover:text-white transition-colors" data-testid="nav-login-btn">
                Sign In
              </Link>
              <button onClick={() => go("#join")} className="btn-skew bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-accent text-lg tracking-wider px-5 py-2 transition-colors duration-200 glow-green" data-testid="nav-join-btn">
                <span>Join the Universe</span>
              </button>
            </>
          )}
          {user && user.role === "admin" && (
            <button onClick={() => navigate("/admin")} className="flex items-center gap-2 font-accent text-lg tracking-wider text-amber-400 hover:text-amber-300" data-testid="nav-admin-btn">
              <ShieldCheck size={18} /> Admin
            </button>
          )}
          {user && (
            <button onClick={logout} className="flex items-center gap-2 btn-skew bg-white/10 hover:bg-white/20 text-white font-accent text-lg tracking-wider px-4 py-2 transition-colors" data-testid="nav-logout-btn">
              <span className="flex items-center gap-2"><LogOut size={16} /> {user.name?.split(" ")[0] || "Hero"}</span>
            </button>
          )}
        </div>

        <div className="md:hidden flex items-center gap-4">
          <button onClick={() => setCartOpen(true)} className="relative text-white" data-testid="nav-cart-btn-mobile" aria-label="cart">
            <ShoppingBag size={24} />
            {count > 0 && <span className="absolute -top-2 -right-2 bg-emerald-500 text-emerald-950 text-xs font-bold rounded-full w-5 h-5 grid place-items-center">{count}</span>}
          </button>
          <button className="text-white" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/10 px-6 py-6 flex flex-col gap-4" data-testid="nav-mobile-menu">
          {links.map((l) => (
            <button key={l.href} onClick={() => go(l.href)} className="font-accent text-2xl text-left text-gray-200 hover:text-emerald-400">
              {l.label}
            </button>
          ))}
          <Link to="/merch" onClick={() => setOpen(false)} className="font-accent text-2xl text-left text-amber-400">Store</Link>
          <div className="h-px bg-white/10 my-2" />
          {user === false ? (
            <Link to="/login" className="font-accent text-2xl text-emerald-400" onClick={() => setOpen(false)}>Sign In</Link>
          ) : (
            <button onClick={logout} className="font-accent text-2xl text-left text-white">Log Out</button>
          )}
          {user?.role === "admin" && <button onClick={() => navigate("/admin")} className="font-accent text-2xl text-left text-amber-400">Admin</button>}
        </div>
      )}
    </motion.header>
  );
};
